import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, ChevronsUpDown, Crown, Info, Mail, MoreHorizontal, Pencil, RefreshCw, Search, Trash2, UserPlus, X } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { fetchMe, invalidateMeCache } from '../../../lib/meClient';
import { UserInviteModal } from '../components/UserInviteModal';
import { UserMemberEditModal } from '../components/UserMemberEditModal';
import type { RolePresetOption } from '../components/RolePresetSelect';
import { listCapabilityDeviations, type CapabilityMap } from '../capabilitiesUi';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../utils/settingsSectionStyles';

type MemberRole = 'owner' | 'admin' | 'member';
/** Which surfaces a user may access. Mirrors the backend `platform_access`. */
type PlatformAccess = 'office_and_field' | 'field_only';
type RolePreset = RolePresetOption;

interface Member {
  user_id: string;
  email: string;
  phone?: string | null;
  display_name: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role: MemberRole;
  platform_access: PlatformAccess;
  role_preset_id?: string | null;
  role_preset?: RolePreset | null;
  capability_overrides?: CapabilityMap;
  joined_at: string;
  last_seen_at: string | null;
  invited?: boolean;
  invitation_last_sent_at?: string | null;
  is_payroll_approver?: boolean;
}

/** Must match server `INVITATION_RESEND_COOLDOWN_MS`. */
const RESEND_COOLDOWN_MS = 5 * 60 * 1000;

function resendCooldownRemainingMs(lastSentAt: string | null | undefined): number {
  if (!lastSentAt) return 0;
  const sent = new Date(lastSentAt).getTime();
  if (Number.isNaN(sent)) return 0;
  return Math.max(0, RESEND_COOLDOWN_MS - (Date.now() - sent));
}

function formatResendCooldown(remainingMs: number): string {
  const totalSec = Math.ceil(remainingMs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min} Min.${sec > 0 ? ` ${sec} Sek.` : ''}`;
  return `${sec} Sek.`;
}

const ROLE_LABEL: Record<MemberRole, string> = {
  owner: 'Inhaber',
  admin: 'Admin',
  member: 'Mitglied',
};

const ACCESS_LABEL: Record<PlatformAccess, string> = {
  office_and_field: 'Büro & App',
  field_only: 'Nur App',
};

/**
 * Farbige Pills sind Ausnahmen vorbehalten (Inhaber/Admin, eingeschränkter
 * Zugang, Abweichungen). Regelfälle bleiben Text, sonst trägt Farbe keine
 * Information mehr.
 */
const ROLE_PILL: Partial<Record<MemberRole, string>> = {
  owner: 'bg-violet-100 text-violet-700',
  admin: 'bg-blue-100 text-blue-700',
};

const ACCESS_PILL: Partial<Record<PlatformAccess, string>> = {
  field_only: 'bg-amber-100 text-amber-700',
};

function pillClass(tone: string, dense = false): string {
  return `inline-flex items-center rounded-full px-2 py-0.5 font-medium ${dense ? 'text-[10px]' : 'text-xs'} ${tone}`;
}

function MemberValueCell({
  label,
  tone,
  dense = false,
}: {
  label: string;
  tone?: string;
  dense?: boolean;
}) {
  if (!tone) {
    return <span className={`${dense ? 'text-[11px]' : 'text-xs'} text-app-text-muted`}>{label}</span>;
  }
  return <span className={pillClass(tone, dense)}>{label}</span>;
}

type DateSortColumn = 'last_seen_at' | 'joined_at';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function compareIsoDates(a: string | null, b: string | null, dir: 'asc' | 'desc'): number {
  const aMissing = !a;
  const bMissing = !b;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  const aTime = new Date(a).getTime();
  const bTime = new Date(b).getTime();
  const diff = (Number.isNaN(aTime) ? 0 : aTime) - (Number.isNaN(bTime) ? 0 : bTime);
  return dir === 'asc' ? diff : -diff;
}

/** Muss auf dem `th` selbst sitzen: der Browser-Default `font-weight: bold` schlaegt geerbtes `font-medium`. */
const TABLE_TH_CLASS = `px-3 py-2 text-left ${SETTINGS_TABLE_HEADER}`;

function SortableDateHeader({
  label,
  column,
  activeColumn,
  dir,
  onSort,
}: {
  label: string;
  column: DateSortColumn;
  activeColumn: DateSortColumn;
  dir: 'asc' | 'desc';
  onSort: (column: DateSortColumn) => void;
}) {
  const active = activeColumn === column;
  return (
    <th className={TABLE_TH_CLASS}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 ${SETTINGS_TABLE_HEADER} hover:text-app-text transition-colors`}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="w-3.5 h-3.5 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-slate-400" aria-hidden />
        )}
      </button>
    </th>
  );
}

function getInitials(name: string | null, email: string): string {
  const src = name?.trim() || email;
  const parts = src.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase() || '?';
}

function memberContactEmail(email: string | null | undefined): string {
  const value = String(email ?? '').trim();
  if (!value || value.endsWith('@phone.invalid')) return '';
  return value;
}

function memberPrimaryLabel(member: Member): string {
  const name = member.display_name?.trim();
  if (name) return name;
  const phone = String(member.phone ?? '').trim();
  if (phone) return phone;
  const email = memberContactEmail(member.email);
  if (email) return email.includes('@') ? email.split('@')[0] : email;
  return '—';
}

function memberSecondaryLabel(member: Member): string {
  const phone = String(member.phone ?? '').trim();
  const email = memberContactEmail(member.email);
  const name = member.display_name?.trim();
  // Login-Kanal: echte Mail → E-Mail-Login; sonst Telefon-Login (@phone.invalid).
  if (email) return email;
  if (phone) {
    // Primärzeile zeigt schon die Nummer → keine Doppelung
    if (!name) return '';
    return phone;
  }
  return '';
}

function memberHasCapabilityOverrides(member: Member): boolean {
  const overrides = member.capability_overrides ?? {};
  const presetCaps = member.role_preset?.capabilities ?? {};
  return listCapabilityDeviations(presetCaps, overrides).length > 0;
}

function MemberRolePresetBadge({ member, dense = false }: { member: Member; dense?: boolean }) {
  // Admins follow their role preset; only owners (and admins without one) have full access.
  const isFullAccess = member.role === 'owner' || (member.role === 'admin' && !member.role_preset);
  const label = isFullAccess ? 'Vollzugriff' : member.role_preset?.name?.trim() || '—';
  const adapted = !isFullAccess && memberHasCapabilityOverrides(member);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={`${dense ? 'text-[11px]' : 'text-xs'} text-app-text-muted`}>{label}</span>
      {adapted ? (
        <span
          className={pillClass('bg-amber-100 text-amber-700', true)}
          title="Individuelle Abweichungen von der Rolle"
        >
          angepasst
        </span>
      ) : null}
    </div>
  );
}

/**
 * Zeilenaktionen im Overflow-Menü: hält die Tabelle ruhig und schützt die
 * destruktive Aktion vor Fehlklicks. Panel per Portal, weil der Tabellen-
 * Container scrollt und das Menü sonst abgeschnitten würde.
 */
function MemberRowActionsMenu({
  canEdit,
  canRemove,
  removeLabel,
  onEdit,
  onRemove,
}: {
  canEdit: boolean;
  canRemove: boolean;
  removeLabel: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const width = 200;
      const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
      setPos({ top: rect.bottom + 4, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEscapeClose(() => setOpen(false), open);

  if (!canEdit && !canRemove) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded p-1 text-slate-400 hover:bg-app-hover hover:text-app-text-muted"
        aria-label="Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              style={{ top: pos.top, left: pos.left }}
              className="fixed z-[80] w-[200px] rounded-lg border border-app-border bg-app-surface p-1 shadow-lg"
            >
              {canEdit ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onEdit();
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
                >
                  <Pencil className="h-4 w-4 text-app-text-subtle" />
                  Bearbeiten
                </button>
              ) : null}
              {canRemove ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onRemove();
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {removeLabel}
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500',
];

function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function ResendInviteButton({
  member,
  loading,
  onResend,
  className = 'p-1 rounded hover:bg-app-hover',
}: {
  member: Member;
  loading: boolean;
  onResend: (member: Member) => void;
  className?: string;
}) {
  const cooldownMs = resendCooldownRemainingMs(member.invitation_last_sent_at);
  const disabled = cooldownMs > 0 || loading;
  const title = cooldownMs > 0
    ? `Erneut senden in ${formatResendCooldown(cooldownMs)}`
    : 'Einladung erneut senden';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onResend(member)}
      className={`${className} disabled:opacity-40 disabled:cursor-not-allowed`}
      aria-label={title}
      title={title}
    >
      {loading ? <LoadingSpinner className="text-app-text-subtle" /> : <RefreshCw className="w-4 h-4 text-app-text-subtle" />}
    </button>
  );
}

export function UsersSettingsSection() {
  const [members, setMembers] = React.useState<Member[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [dateSortColumn, setDateSortColumn] = React.useState<DateSortColumn>('last_seen_at');
  const [dateSortDir, setDateSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
  const [myCapabilities, setMyCapabilities] = React.useState<Record<string, boolean>>({});

  // Member edit modal
  const [editTarget, setEditTarget] = React.useState<Member | null>(null);
  const [roleSaving, setRoleSaving] = React.useState(false);

  // Remove confirm
  const [removeTarget, setRemoveTarget] = React.useState<Member | null>(null);
  const [removeLoading, setRemoveLoading] = React.useState(false);

  // Transfer ownership confirm
  const [transferTarget, setTransferTarget] = React.useState<Member | null>(null);
  const [transferLoading, setTransferLoading] = React.useState(false);

  useEscapeClose(() => {
    if (!removeLoading) setRemoveTarget(null);
  }, Boolean(removeTarget));

  useEscapeClose(() => {
    if (!transferLoading) setTransferTarget(null);
  }, Boolean(transferTarget));

  // Invite modal
  const [showInvite, setShowInvite] = React.useState(false);
  const [inviteLoading, setInviteLoading] = React.useState(false);
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = React.useState(false);
  const [rolePresets, setRolePresets] = React.useState<RolePreset[]>([]);

  const [resendUserId, setResendUserId] = React.useState<string | null>(null);
  const [resendTick, setResendTick] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meRes, membersRes, presetsRes] = await Promise.all([
        fetch('/api/me', { credentials: 'include' }),
        fetch('/api/me/members', { credentials: 'include' }),
        fetch('/api/me/role-presets', { credentials: 'include' }),
      ]);
      if (meRes.ok) {
        const me = await meRes.json() as { user?: { id?: string }; capabilities?: Record<string, boolean> };
        setCurrentUserId(me.user?.id ?? null);
        setMyCapabilities(me.capabilities ?? {});
      }
      if (!membersRes.ok) throw new Error('Mitglieder konnten nicht geladen werden.');
      const data = await membersRes.json() as { members: Member[] };
      setMembers(data.members ?? []);
      if (presetsRes.ok) {
        const presetData = await presetsRes.json() as { presets?: RolePreset[] };
        setRolePresets(presetData.presets ?? []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const hasResendCooldown = React.useMemo(
    () => members.some((m) => m.invited && resendCooldownRemainingMs(m.invitation_last_sent_at) > 0),
    [members, resendTick],
  );

  React.useEffect(() => {
    if (!hasResendCooldown) return;
    const id = window.setInterval(() => setResendTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [hasResendCooldown]);

  const myRole = members.find((m) => m.user_id === currentUserId)?.role ?? 'member';
  const canManage = myRole === 'owner' || myRole === 'admin' || Boolean(myCapabilities['web.einstellungen.edit']);
  const canTransferOwnership = myRole === 'owner';
  const canInvite = Boolean(myCapabilities['web.einstellungen.invite']);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.email.toLowerCase().includes(q) ||
        String(m.phone ?? '').toLowerCase().includes(q) ||
        (m.display_name ?? '').toLowerCase().includes(q),
    );
  }, [members, search]);

  const sortedFiltered = React.useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => compareIsoDates(a[dateSortColumn], b[dateSortColumn], dateSortDir));
    return list;
  }, [filtered, dateSortColumn, dateSortDir]);

  const handleDateSort = (column: DateSortColumn) => {
    if (dateSortColumn === column) {
      setDateSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setDateSortColumn(column);
      setDateSortDir('desc');
    }
  };

  const handleMemberSave = async (payload: {
    role: MemberRole;
    platform_access: PlatformAccess;
    role_preset_id: string | null;
    capability_overrides: CapabilityMap;
    is_payroll_approver: boolean;
    first_name: string;
    last_name: string;
    display_name: string;
    email?: string;
    phone?: string;
  }) => {
    if (!editTarget) return;
    if (payload.role === 'owner' && editTarget.role !== 'owner') {
      if (
        !canTransferOwnership ||
        editTarget.platform_access !== 'office_and_field' ||
        payload.platform_access !== 'office_and_field'
      ) {
        setError('Inhaberschaft kann nur an Nutzer mit Zugang „Büro & App“ übertragen werden.');
        return;
      }
      setError(null);
      setTransferTarget(editTarget);
      setEditTarget(null);
      return;
    }
    setRoleSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/me/members/${editTarget.user_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Only send the role when it changes; ownership moves via the transfer flow.
          ...(payload.role !== editTarget.role ? { role: payload.role } : {}),
          platform_access: payload.platform_access,
          role_preset_id: payload.role_preset_id,
          // Server merges overrides; null clears keys removed in the UI (inherit preset again).
          capability_overrides: (() => {
            const previous = editTarget.capability_overrides ?? {};
            const patch: Record<string, boolean | null> = { ...payload.capability_overrides };
            for (const key of Object.keys(previous)) {
              if (!(key in payload.capability_overrides)) patch[key] = null;
            }
            return patch;
          })(),
          is_payroll_approver: payload.is_payroll_approver,
          first_name: payload.first_name,
          last_name: payload.last_name,
          ...(payload.email ? { email: payload.email } : {}),
          ...(payload.phone ? { phone: payload.phone } : {}),
        }),
      });
      const responsePayload = await res.json().catch(() => null) as {
        error?: string;
        email?: string;
        phone?: string;
        sms_sent?: boolean;
        warning?: string;
        display_name?: string;
        first_name?: string;
        last_name?: string;
      } | null;
      if (!res.ok) throw new Error(responsePayload?.error ?? 'Mitglied konnte nicht aktualisiert werden.');
      const nextPreset = rolePresets.find((preset) => preset.id === payload.role_preset_id) ?? null;
      const nextEmail = responsePayload?.email ?? payload.email ?? editTarget.email;
      const nextDisplayName = responsePayload?.display_name ?? payload.display_name;
      const nextFirstName = responsePayload?.first_name ?? payload.first_name;
      const nextLastName = responsePayload?.last_name ?? payload.last_name;
      setMembers((prev) =>
        prev.map((m) =>
          m.user_id === editTarget.user_id
            ? {
                ...m,
                role: payload.role,
                platform_access: payload.platform_access,
                role_preset_id: payload.role_preset_id,
                role_preset: nextPreset,
                capability_overrides: payload.capability_overrides,
                is_payroll_approver: payload.role === 'owner' ? true : payload.is_payroll_approver,
                email: nextEmail,
                ...(responsePayload?.phone ? { phone: responsePayload.phone } : {}),
                display_name: nextDisplayName,
                first_name: nextFirstName,
                last_name: nextLastName,
              }
            : m,
        ),
      );
      setEditTarget(null);
      if (responsePayload?.phone && responsePayload.sms_sent === false) {
        setError(`Telefonnummer geändert, aber SMS fehlgeschlagen: ${responsePayload.warning ?? 'unbekannter Fehler'}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setRoleSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    try {
      const res = await fetch(`/api/me/members/${removeTarget.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Mitglied konnte nicht entfernt werden.');
      setMembers((prev) => prev.filter((m) => m.user_id !== removeTarget.user_id));
      setRemoveTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleResendInvite = async (member: Member) => {
    setResendUserId(member.user_id);
    setError(null);
    try {
      const res = await fetch(`/api/me/members/${member.user_id}/resend-invite`, {
        method: 'POST',
        credentials: 'include',
      });
      const payload = await res.json().catch(() => null) as {
        error?: string;
        retry_after_seconds?: number;
        invitation_last_sent_at?: string;
      } | null;
      if (!res.ok) {
        throw new Error(payload?.error ?? 'Einladung konnte nicht erneut gesendet werden.');
      }
      const sentAt = payload?.invitation_last_sent_at ?? new Date().toISOString();
      setMembers((prev) =>
        prev.map((m) =>
          m.user_id === member.user_id ? { ...m, invitation_last_sent_at: sentAt } : m,
        ),
      );
      setResendTick((t) => t + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setResendUserId(null);
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget) return;
    setTransferLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/me/members/${encodeURIComponent(transferTarget.user_id)}/transfer-ownership`, {
        method: 'POST',
        credentials: 'include',
      });
      const payload = await res.json().catch(() => null) as { error?: string } | null;
      if (!res.ok) throw new Error(payload?.error ?? 'Inhaberschaft konnte nicht übertragen werden.');
      setTransferTarget(null);
      invalidateMeCache();
      await fetchMe({ force: true });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleInvite = async (payload: {
    channel: 'email' | 'phone';
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
    full_name: string;
    role: 'member' | 'admin';
    platform_access: PlatformAccess;
    role_preset_id: string | null;
    capability_overrides: CapabilityMap;
  }) => {
    setInviteError(null);
    setInviteLoading(true);
    try {
      const body =
        payload.channel === 'phone'
          ? {
              phone: payload.phone,
              first_name: payload.first_name,
              last_name: payload.last_name,
              full_name: payload.full_name,
              role: payload.role,
              platform_access: payload.platform_access,
              role_preset_id: payload.role_preset_id,
              capability_overrides: payload.capability_overrides,
            }
          : {
              email: payload.email,
              first_name: payload.first_name,
              last_name: payload.last_name,
              full_name: payload.full_name,
              role: payload.role,
              platform_access: payload.platform_access,
              role_preset_id: payload.role_preset_id,
              capability_overrides: payload.capability_overrides,
            };
      const res = await fetch('/api/me/invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const responsePayload = await res.json().catch(() => null) as {
        error?: string;
        warning?: string | null;
        sms_sent?: boolean;
      } | null;
      if (!res.ok) throw new Error(responsePayload?.error ?? 'Einladung fehlgeschlagen.');
      if (responsePayload?.warning) {
        setInviteError(
          responsePayload.sms_sent === false
            ? `Zugang angelegt, aber SMS fehlgeschlagen: ${responsePayload.warning}`
            : responsePayload.warning,
        );
        void load();
        return;
      }
      setInviteSuccess(true);
      void load();
      setTimeout(() => { resetInviteModal(); }, 2500);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setInviteLoading(false);
    }
  };

  const openInviteModal = () => {
    if (!canInvite) return;
    setInviteError(null);
    setInviteSuccess(false);
    setShowInvite(true);
  };

  const resetInviteModal = () => {
    setShowInvite(false);
    setInviteError(null);
    setInviteSuccess(false);
  };

  return (
    <>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex justify-between items-center">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-500 hover:underline">
            Schließen
          </button>
        </div>
      )}

      <section className="-mx-3 -mt-3 md:mx-0 md:mt-0 bg-app-surface border-0 md:border md:border-app-border md:rounded-xl overflow-hidden">
        {/* Section header */}
        <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-4">
          <h2 className="text-base font-bold text-app-text">
            Nutzer{!loading && members.length > 0 ? ` (${members.length})` : ''}
          </h2>
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nutzer suchen…"
                className={`w-52 ${SETTINGS_INPUT_CLASS} pl-7`}
              />
            </div>
            {canInvite && (
              <button
                type="button"
                onClick={openInviteModal}
                className={`hidden md:inline-flex ${SETTINGS_BTN_PRIMARY}`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                Einladen
              </button>
            )}
          </div>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-app-border">
          {loading ? (
            <div className="px-4 py-4">
              <InlineLoadingState label="Wird geladen…" className="text-xs" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-4 text-center text-sm text-app-text-subtle">
              {search ? 'Keine Ergebnisse gefunden.' : 'Noch keine Mitglieder vorhanden.'}
            </div>
          ) : (
            sortedFiltered.map((member) => {
              const isMe = member.user_id === currentUserId;
              const isInvited = !!member.invited;
              const primary = memberPrimaryLabel(member);
              const secondary = memberSecondaryLabel(member);
              return (
                <div key={member.user_id} className={`px-4 py-3 flex items-center gap-3 ${isInvited ? 'opacity-75' : ''}`}>
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isInvited ? 'bg-amber-100 text-amber-600' : `${avatarColor(member.user_id)} text-white`}`}
                  >
                    {isInvited ? <Mail className="w-4 h-4" /> : getInitials(member.display_name, secondary || primary)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-text truncate">
                      {primary}
                      {isMe && <span className="text-xs font-normal text-slate-400 ml-1">(du)</span>}
                    </p>
                    {secondary ? (
                      <p className="text-xs text-app-text-subtle truncate">{secondary}</p>
                    ) : null}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <MemberRolePresetBadge member={member} dense />
                    <MemberValueCell
                      label={ACCESS_LABEL[member.platform_access]}
                      tone={ACCESS_PILL[member.platform_access]}
                      dense
                    />
                    <MemberValueCell label={ROLE_LABEL[member.role]} tone={ROLE_PILL[member.role]} dense />
                    {canInvite && isInvited && (
                      <ResendInviteButton
                        member={member}
                        loading={resendUserId === member.user_id}
                        onResend={(m) => { void handleResendInvite(m); }}
                        className="p-1.5 rounded hover:bg-app-hover"
                      />
                    )}
                    {canManage && !isMe && (
                      <button
                        type="button"
                        onClick={() => setRemoveTarget(member)}
                        className="p-1.5 rounded hover:bg-red-50"
                        aria-label="Entfernen"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Table (desktop only) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className={`bg-app-surface-muted border-b border-app-border ${SETTINGS_TABLE_HEADER}`}>
              <tr>
                <th className={TABLE_TH_CLASS}>Nutzername</th>
                <th className={TABLE_TH_CLASS}>Mitgliedschaft</th>
                <th className={TABLE_TH_CLASS}>Rolle</th>
                <th className={TABLE_TH_CLASS}>Zugang</th>
                <th className={TABLE_TH_CLASS}>Lohnbuchhaltung</th>
                <SortableDateHeader
                  label="Zuletzt aktiv"
                  column="last_seen_at"
                  activeColumn={dateSortColumn}
                  dir={dateSortDir}
                  onSort={handleDateSort}
                />
                <SortableDateHeader
                  label="Hinzugefügt"
                  column="joined_at"
                  activeColumn={dateSortColumn}
                  dir={dateSortDir}
                  onSort={handleDateSort}
                />
                <th className="px-3 py-2 text-right w-24" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-4">
                    <InlineLoadingState label="Wird geladen…" className="text-xs" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-sm text-app-text-subtle">
                    {search ? 'Keine Ergebnisse gefunden.' : 'Noch keine Mitglieder vorhanden.'}
                  </td>
                </tr>
              ) : (
                sortedFiltered.map((member) => {
                  const isMe = member.user_id === currentUserId;
                  const isInvited = !!member.invited;
                  const primary = memberPrimaryLabel(member);
                  const secondary = memberSecondaryLabel(member);
                  const canOpenEdit = canManage && !isInvited;
                  return (
                    <tr
                      key={member.user_id}
                      className={`border-b border-app-border hover:bg-app-hover ${isInvited ? 'opacity-75' : ''} ${canOpenEdit ? 'cursor-pointer focus-visible:bg-app-surface-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-pipedrive-blue' : ''}`}
                      tabIndex={canOpenEdit ? 0 : undefined}
                      role={canOpenEdit ? 'button' : undefined}
                      aria-label={canOpenEdit ? `${primary} bearbeiten` : undefined}
                      onClick={
                        canOpenEdit
                          ? () => {
                              setError(null);
                              setEditTarget(member);
                            }
                          : undefined
                      }
                      onKeyDown={
                        canOpenEdit
                          ? (e) => {
                              // Nicht auf Tasten reagieren, die aus Buttons in der Zeile hochblubbern.
                              if (e.target !== e.currentTarget) return;
                              if (e.key !== 'Enter' && e.key !== ' ') return;
                              e.preventDefault();
                              setError(null);
                              setEditTarget(member);
                            }
                          : undefined
                      }
                    >
                      {/* Avatar + name + email */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isInvited ? 'bg-amber-100 text-amber-600' : `${avatarColor(member.user_id)} text-white`}`}
                          >
                            {isInvited ? <Mail className="w-4 h-4" /> : getInitials(member.display_name, secondary || primary)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-app-text truncate leading-tight flex items-center gap-1.5 flex-wrap">
                              {primary}
                              {isMe && <span className="text-xs font-normal text-slate-400">(du)</span>}
                              {isInvited && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                  <Mail className="w-2.5 h-2.5" />
                                  Eingeladen
                                </span>
                              )}
                            </div>
                            {secondary ? (
                              <div className="text-xs text-app-text-subtle truncate">{secondary}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      {/* Mitgliedschaft */}
                      <td className="px-3 py-2">
                        <MemberValueCell label={ROLE_LABEL[member.role]} tone={ROLE_PILL[member.role]} />
                      </td>

                      <td className="px-3 py-2">
                        <MemberRolePresetBadge member={member} />
                      </td>

                      {/* Platform access */}
                      <td className="px-3 py-2">
                        <MemberValueCell
                          label={ACCESS_LABEL[member.platform_access]}
                          tone={ACCESS_PILL[member.platform_access]}
                        />
                      </td>

                      {/* Payroll approver */}
                      <td className="px-3 py-2">
                        {isInvited ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <span className={`text-xs ${member.is_payroll_approver ? 'text-app-text-muted' : 'text-slate-400'}`}>
                            {member.is_payroll_approver ? 'Ja' : 'Nein'}
                          </span>
                        )}
                      </td>

                      {/* Last active */}
                      <td className="px-3 py-2 text-xs text-app-text-muted">
                        {isInvited ? <span className="text-slate-400 italic">Ausstehend</span> : formatDate(member.last_seen_at)}
                      </td>

                      {/* Joined / invited */}
                      <td className="px-3 py-2 text-xs text-app-text-muted">{formatDate(member.joined_at)}</td>

                      {/* Actions */}
                      <td
                        className="px-3 py-2 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {canInvite && isInvited && (
                            <ResendInviteButton
                              member={member}
                              loading={resendUserId === member.user_id}
                              onResend={(m) => { void handleResendInvite(m); }}
                            />
                          )}
                          {canManage ? (
                            <MemberRowActionsMenu
                              canEdit={!isInvited && member.role !== 'owner'}
                              canRemove={!isMe && member.role !== 'owner'}
                              removeLabel={isInvited ? 'Einladung widerrufen' : 'Entfernen'}
                              onEdit={() => {
                                setError(null);
                                setEditTarget(member);
                              }}
                              onRemove={() => setRemoveTarget(member)}
                            />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {!loading && filtered.length > 0 ? (
            <div className="flex items-start gap-2 border-t border-app-border px-3 py-2.5 text-xs text-app-text-subtle">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
              <p>
                „angepasst“ = individuelle Abweichungen von der Rolle. Inhaber und Admin haben immer Vollzugriff.
              </p>
            </div>
          ) : null}
        </div>
      </section>

      {editTarget ? (
        <UserMemberEditModal
          member={editTarget}
          presets={rolePresets}
          initials={getInitials(editTarget.display_name, memberSecondaryLabel(editTarget) || memberPrimaryLabel(editTarget))}
          avatarClassName={avatarColor(editTarget.user_id)}
          contactLine={memberSecondaryLabel(editTarget) || memberContactEmail(editTarget.email)}
          displayName={editTarget.display_name?.trim() || ''}
          saving={roleSaving}
          saveError={error}
          canTransferOwnership={
            canTransferOwnership && editTarget.platform_access === 'office_and_field'
          }
          onClose={() => !roleSaving && setEditTarget(null)}
          onSave={handleMemberSave}
        />
      ) : null}

      {showInvite && canInvite ? (
        <UserInviteModal
          canManage={canManage}
          presets={rolePresets}
          loading={inviteLoading}
          success={inviteSuccess}
          error={inviteError}
          onClose={() => !inviteLoading && resetInviteModal()}
          onSubmit={handleInvite}
        />
      ) : null}

      {/* ── Remove Confirm Modal ── */}
      {removeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => !removeLoading && setRemoveTarget(null)}
          role="presentation"
        >
          <div
            className="relative w-full max-w-md rounded-xl border border-app-border bg-app-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setRemoveTarget(null)}
              disabled={removeLoading}
              className="absolute right-3 top-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center gap-5 px-2 text-center">
              <h2 className="text-base font-bold text-app-text">
                {removeTarget.invited ? 'Einladung widerrufen' : 'Nutzer entfernen'}
              </h2>
              <p className="text-sm leading-relaxed text-app-text-muted">
                {removeTarget.invited
                  ? <>Möchtest du die Einladung für <span className="font-medium text-app-text">{memberPrimaryLabel(removeTarget)}</span> widerrufen?</>
                  : (
                    <>
                      Möchtest du <span className="font-medium text-app-text">{memberPrimaryLabel(removeTarget)}</span> aus diesem Betrieb entfernen?
                      {' '}Nur der Zugang wird entfernt; ein verknüpftes Mitarbeiterprofil bleibt erhalten.
                      Der Zugang ist 28 Tage im Papierkorb wiederherstellbar.
                    </>
                  )}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  disabled={removeLoading}
                  onClick={() => setRemoveTarget(null)}
                  className={SETTINGS_BTN_OUTLINE}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  disabled={removeLoading}
                  onClick={() => { void handleRemove(); }}
                  className={SETTINGS_BTN_DANGER_OUTLINE}
                >
                  {removeLoading ? 'Wird entfernt…' : (removeTarget.invited ? 'Einladung widerrufen' : 'Entfernen')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Transfer Ownership Confirm Modal ── */}
      {transferTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => !transferLoading && setTransferTarget(null)}
          role="presentation"
        >
          <div
            className="relative w-full max-w-md rounded-xl border border-app-border bg-app-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setTransferTarget(null)}
              disabled={transferLoading}
              className="absolute right-3 top-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center gap-5 px-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                <Crown className="h-6 w-6" />
              </div>
              <h2 className="text-base font-bold text-app-text">Inhaberschaft übertragen</h2>
              <p className="text-sm leading-relaxed text-app-text-muted">
                <span className="font-medium text-app-text">{memberPrimaryLabel(transferTarget)}</span> wird
                neuer Inhaber dieses Betriebs. Du wirst danach Admin (Vollzugriff, aber nicht mehr Inhaber).
                Die Aktion lässt sich nur rückgängig machen, wenn der neue Inhaber die Rolle wieder an dich
                überträgt.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  disabled={transferLoading}
                  onClick={() => setTransferTarget(null)}
                  className={SETTINGS_BTN_OUTLINE}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  disabled={transferLoading}
                  onClick={() => { void handleTransferOwnership(); }}
                  className="inline-flex h-9 items-center justify-center gap-2 shrink-0 rounded-md bg-violet-600 px-3.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-40 transition-colors"
                >
                  {transferLoading ? 'Wird übertragen…' : 'Inhaberschaft übertragen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Mobile FAB — invite new user */}
      {canInvite && (
        <button
          type="button"
          onClick={openInviteModal}
          className="fixed bottom-20 right-5 z-30 md:hidden h-14 w-14 rounded-full bg-pipedrive-blue text-white shadow-lg flex items-center justify-center hover:bg-blue-700 active:bg-blue-800"
          aria-label="Nutzer einladen"
        >
          <UserPlus className="w-6 h-6" />
        </button>
      )}
    </>
  );
}
